import { useCallback, useEffect, useState } from "react";
import { api, formatApiError, RESOURCE_CATEGORIES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import MetadataFooter from "@/components/MetadataFooter";
import { Plus, Search, BookOpen, Phone, Mail, MapPin, ExternalLink, X, LayoutGrid, List as ListIcon, Pencil, Upload, Trash2, AlertTriangle, ShieldCheck, ShieldAlert, BellRing, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";
import ResourceImportDialog from "@/components/ResourceImportDialog";

const VERIFICATION_DUE_SOON_DAYS = 30;
function classifyVerification(r) {
  const lv = r?.last_verified_at;
  const nd = r?.next_verification_due;
  if (!lv || !nd) return "never_verified";
  const due = new Date(nd);
  if (isNaN(due.getTime())) return "never_verified";
  const daysLeft = Math.floor((due.getTime() - Date.now()) / 86400000);
  if (daysLeft < 0) return "overdue";
  if (daysLeft <= VERIFICATION_DUE_SOON_DAYS) return "due_soon";
  return "canonical";
}

export default function Resources() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("grid"); // 'grid' | 'list'
  const [showImport, setShowImport] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verifyCandidate, setVerifyCandidate] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (category) params.category = category;
      const { data } = await api.get("/resources", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, category]);

  // Refresh the unfiltered total separately so the header reflects every
  // resource in the directory, not just the current filter result.
  const loadTotal = async () => {
    try {
      const { data } = await api.get("/resources");
      setTotalCount(Array.isArray(data) ? data.length : 0);
    } catch {
      // Silent fail — the header just won't show a count.
    }
  };
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTotal(); }, []);

  const confirmDelete = async () => {
    if (!deleteCandidate) return;
    setDeleting(true);
    try {
      await api.delete(`/resources/${deleteCandidate.id}`, { data: { reason: "Removed from directory" } });
      toast.success(`Deleted "${deleteCandidate.name}". You can restore from Recently Deleted.`);
      setDeleteCandidate(null);
      await Promise.all([load(), loadTotal()]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const isFiltered = Boolean(q || category || needsVerification);
  const filteredItems = needsVerification
    ? items.filter((r) => ["overdue", "never_verified"].includes(classifyVerification(r)))
    : items;

  const sendReminders = async () => {
    setSendingReminder(true);
    try {
      const { data } = await api.post("/admin/resources/verification-reminder");
      if (data.overdue_or_never === 0) {
        toast.success("Nothing overdue — no reminders needed.");
      } else {
        toast.success(`Reminder sent to ${data.sent} admin${data.sent === 1 ? "" : "s"} (${data.overdue_or_never} resource${data.overdue_or_never === 1 ? "" : "s"} need verification)`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingReminder(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="resources-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="hw-eyebrow mb-2">Community connections</div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Resource Directory</h1>
            {totalCount !== null && (
              <span
                data-testid="resources-total-count"
                className="inline-flex items-center px-2.5 py-1 rounded-full bg-[#E8ECE7] text-[#1E3A8A] text-xs font-semibold uppercase tracking-wider"
              >
                {totalCount} {totalCount === 1 ? "resource" : "resources"}
              </span>
            )}
          </div>
        </div>
        {can("resources.create") && (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowImport(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-[#1E3A8A] text-[#1E3A8A] text-sm font-medium hover:bg-[#F9F8F6]"
              data-testid="import-resources-btn"
            >
              <Upload className="w-4 h-4" /> Import spreadsheet
            </button>
            <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium" data-testid="add-resource-btn">
              <Plus className="w-4 h-4" /> Add resource
            </button>
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0]">
          <Search className="w-4 h-4 text-[#1E3A8A]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search resources…" className="bg-transparent flex-1 outline-none text-sm" data-testid="resources-search" />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] text-sm" data-testid="resources-category-filter">
          <option value="">All categories</option>
          {RESOURCE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <button onClick={load} className="px-4 py-2 rounded-xl bg-[#E8ECE7] text-[#1E3A8A] text-sm font-medium">Search</button>

        {can("resources.edit") && (
          <>
            <button
              onClick={() => setNeedsVerification((v) => !v)}
              data-testid="filter-needs-verification"
              title="Toggle: show only resources that are overdue or never verified"
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border ${needsVerification ? "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" : "bg-white text-[#0F172A] border-[#E5E5E0] hover:bg-[#F9F8F6]"}`}
            >
              <ShieldAlert className="w-3.5 h-3.5" />
              Needs verification
            </button>
            <button
              onClick={sendReminders}
              disabled={sendingReminder}
              data-testid="send-verification-reminder"
              title="Email every admin/ED the list of resources that are overdue or never verified"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border bg-white text-[#1E3A8A] border-[#E5E5E0] hover:bg-[#F9F8F6] disabled:opacity-40"
            >
              <BellRing className="w-3.5 h-3.5" />
              {sendingReminder ? "Sending…" : "Email reminders"}
            </button>
          </>
        )}

        <div className="inline-flex p-1 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] self-center" data-testid="resources-view-toggle">
          <button
            onClick={() => setView("grid")}
            data-testid="resources-view-grid-btn"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              view === "grid" ? "bg-white text-[#1E3A8A] shadow-sm" : "text-[#1E3A8A]"
            }`}
            aria-label="Grid view"
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Grid
          </button>
          <button
            onClick={() => setView("list")}
            data-testid="resources-view-list-btn"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              view === "list" ? "bg-white text-[#1E3A8A] shadow-sm" : "text-[#1E3A8A]"
            }`}
            aria-label="List view"
          >
            <ListIcon className="w-3.5 h-3.5" /> List
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-12 text-center">
          <BookOpen className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">No resources yet</h3>
          <p className="text-sm text-[#1E3A8A]">Build your local directory of partner organizations.</p>
        </div>
      ) : filteredItems.length === 0 && needsVerification ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-12 text-center" data-testid="resources-no-verification-needed">
          <ShieldCheck className="w-10 h-10 text-[#166534] mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">Every visible resource is verified.</h3>
          <p className="text-sm text-[#1E3A8A]">Toggle the filter off to see all resources.</p>
        </div>
      ) : (
        <>
          <div className="text-xs text-[#334155]" data-testid="resources-filtered-count">
            {isFiltered && totalCount !== null
              ? `Showing ${filteredItems.length} of ${totalCount} resources${needsVerification ? " · needs verification" : ""}`
              : `Showing ${filteredItems.length} resource${filteredItems.length === 1 ? "" : "s"}`}
          </div>
          {view === "list" ? (
        <ul className="bg-white border border-[#E5E5E0] rounded-2xl divide-y divide-[#F0EFEB] overflow-hidden" data-testid="resources-list-view">
          {filteredItems.map((r) => (
            <li key={r.id} className="p-4 sm:p-5 hover:bg-[#F9F8F6] transition-colors">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-display font-semibold text-[#0F172A]">{r.name}</h3>
                    <span className="text-[10px] bg-[#E8ECE7] text-[#1E3A8A] px-2 py-0.5 rounded-full uppercase tracking-wider">
                      {r.category?.replace(/_/g, " ")}
                    </span>
                    <VerificationChip resource={r} />
                    {r.source === "partner_submission" && r.source_submission_id && (
                      <Link
                        to="/admin/partner-submissions?status=approved"
                        title="View the original partner application"
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#DBEAFE] text-[#1E3A8A] border border-[#BFDBFE]"
                        data-testid={`provenance-link-${r.id}`}
                      >
                        <LinkIcon className="w-3 h-3" /> From application
                      </Link>
                    )}
                  </div>
                  {r.description && <p className="text-sm text-[#334155] mb-2">{r.description}</p>}
                  {r.eligibility && (
                    <p className="text-xs text-[#1E3A8A] mb-2"><strong className="text-[#334155]">Eligibility:</strong> {r.eligibility}</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#334155] sm:text-right sm:flex-col sm:items-end sm:min-w-[180px]">
                  {r.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#1E3A8A]" />{r.phone}</span>}
                  {r.email && <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#1E3A8A]" />{r.email}</span>}
                  {r.address && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#1E3A8A]" />{r.address}</span>}
                  {r.website && (
                    <a href={r.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#1E3A8A] hover:underline">
                      <ExternalLink className="w-3.5 h-3.5" />Website
                    </a>
                  )}
                  {can("resources.edit") && (
                    <button
                      onClick={() => setVerifyCandidate(r)}
                      data-testid={`verify-resource-${r.id}`}
                      className="text-xs text-[#166534] inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#ECFDF5] border border-[#A7F3D0] hover:bg-[#DCFCE7]"
                    >
                      <ShieldCheck className="w-3.5 h-3.5" /> Verify
                    </button>
                  )}
                  {can("resources.edit") && (
                    <button
                      onClick={() => setEditing(r)}
                      data-testid={`edit-resource-${r.id}`}
                      className="text-xs text-[#1E3A8A] inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] hover:bg-white"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                  )}
                  {can("resources.delete") && (
                    <button
                      onClick={() => setDeleteCandidate(r)}
                      data-testid={`delete-resource-${r.id}`}
                      className="text-xs text-[#991B1B] inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#FCA5A5] hover:bg-[#FEF2F2]"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  )}
                </div>
              </div>
              <MetadataFooter entity={r} className="mt-3 pt-2 border-t border-[#F0EFEB]" />
            </li>
          ))}
        </ul>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="resources-list">
          {filteredItems.map((r) => (
            <div key={r.id} className="bg-white border border-[#E5E5E0] rounded-2xl p-5 hw-card-hover">
              <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                <div className="flex flex-wrap items-center gap-1.5">
                  <div className="text-[10px] bg-[#E8ECE7] text-[#1E3A8A] inline-block px-2 py-0.5 rounded-full uppercase tracking-wider">
                    {r.category?.replace(/_/g, " ")}
                  </div>
                  <VerificationChip resource={r} />
                </div>
                <div className="flex items-center gap-1">
                  {can("resources.edit") && (
                    <button
                      onClick={() => setVerifyCandidate(r)}
                      data-testid={`verify-resource-${r.id}`}
                      className="text-xs text-[#166534] inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#ECFDF5] border border-[#A7F3D0] hover:bg-[#DCFCE7]"
                      title="Mark verified"
                    >
                      <ShieldCheck className="w-3 h-3" />
                    </button>
                  )}
                  {can("resources.edit") && (
                    <button
                      onClick={() => setEditing(r)}
                      data-testid={`edit-resource-${r.id}`}
                      className="text-xs text-[#1E3A8A] inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] hover:bg-white"
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                  )}
                  {can("resources.delete") && (
                    <button
                      onClick={() => setDeleteCandidate(r)}
                      data-testid={`delete-resource-${r.id}`}
                      className="text-xs text-[#991B1B] inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-[#FCA5A5] hover:bg-[#FEF2F2]"
                      aria-label={`Delete ${r.name}`}
                      title="Delete (soft delete; restorable)"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
              <h3 className="font-display text-lg font-semibold text-[#0F172A] mb-1">{r.name}</h3>
              {r.source === "partner_submission" && r.source_submission_id && (
                <Link
                  to="/admin/partner-submissions?status=approved"
                  data-testid={`provenance-link-${r.id}`}
                  className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#DBEAFE] text-[#1E3A8A] border border-[#BFDBFE] mb-2"
                  title="View the original partner application"
                >
                  <LinkIcon className="w-3 h-3" /> Created from partner submission
                </Link>
              )}
              {r.description && <p className="text-sm text-[#334155] mb-3">{r.description}</p>}
              <div className="space-y-1 text-sm text-[#334155]">
                {r.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#1E3A8A]" />{r.phone}</div>}
                {r.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#1E3A8A]" />{r.email}</div>}
                {r.address && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#1E3A8A]" />{r.address}</div>}
                {r.website && (
                  <a href={r.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#1E3A8A] hover:underline">
                    <ExternalLink className="w-3.5 h-3.5" />Website
                  </a>
                )}
              </div>
              {r.eligibility && (
                <div className="mt-3 pt-3 border-t border-[#F0EFEB] text-xs text-[#1E3A8A]">
                  <strong className="text-[#334155]">Eligibility:</strong> {r.eligibility}
                </div>
              )}
              <PartnerProfileSection profile={r.partner_profile} />
              <MetadataFooter entity={r} className="mt-3 pt-3 border-t border-[#F0EFEB]" />
            </div>
          ))}
        </div>
      )}
        </>
      )}

      {deleteCandidate && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          data-testid="resource-delete-confirm-modal"
        >
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-[#E5E5E0] flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-[#92400E]" />
              <div className="flex-1">
                <h3 className="font-display text-lg font-semibold text-[#0F172A]">Delete this resource?</h3>
                <p className="text-xs text-[#334155]">It will be removed from the directory and category filters. You can restore it from the Recently Deleted admin page.</p>
              </div>
            </div>
            <div className="px-6 py-4 space-y-2 text-sm">
              <div className="font-medium text-[#0F172A]">{deleteCandidate.name}</div>
              {deleteCandidate.category && (
                <div className="text-xs text-[#334155]">Category: <span className="font-mono">{deleteCandidate.category}</span></div>
              )}
              {deleteCandidate.county && (
                <div className="text-xs text-[#334155]">County: {deleteCandidate.county}</div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-[#E5E5E0] flex items-center justify-end gap-2 bg-[#F9F8F6]">
              <button
                onClick={() => setDeleteCandidate(null)}
                disabled={deleting}
                data-testid="resource-delete-cancel"
                className="px-4 py-2 rounded-full border border-[#E5E5E0] text-sm font-medium text-[#334155] hover:bg-white"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                data-testid="resource-delete-confirm"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#991B1B] text-white text-sm font-medium hover:bg-[#7F1D1D] disabled:opacity-40"
              >
                {deleting ? "Deleting…" : "Delete resource"}
              </button>
            </div>
          </div>
        </div>
      )}

      {verifyCandidate && (
        <VerifyModal
          resource={verifyCandidate}
          onClose={() => setVerifyCandidate(null)}
          onVerified={() => { setVerifyCandidate(null); load(); loadTotal(); }}
        />
      )}

      {showForm && <ResourceForm onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); loadTotal(); }} />}
      {editing && (
        <ResourceForm
          existing={editing}
          onClose={() => setEditing(null)}
          onCreated={() => { setEditing(null); load(); loadTotal(); }}
        />
      )}
      <ResourceImportDialog
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => { load(); loadTotal(); }}
      />
    </div>
  );
}

function ResourceForm({ onClose, onCreated, existing }) {
  const [form, setForm] = useState({
    name: existing?.name || "",
    category: existing?.category || "housing",
    description: existing?.description || "",
    phone: existing?.phone || "",
    email: existing?.email || "",
    website: existing?.website || "",
    address: existing?.address || "",
    county: existing?.county || "",
    eligibility: existing?.eligibility || "",
    hours: existing?.hours || "",
    notes: existing?.notes || "",
    active: existing?.active ?? true,
  });
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!existing;
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.patch(`/resources/${existing.id}`, form);
        toast.success("Resource updated");
      } else {
        await api.post("/resources", form);
        toast.success("Resource added");
      }
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E5E5E0] px-6 py-4 flex justify-between">
          <h2 className="font-display text-xl font-semibold">{isEdit ? "Edit" : "New"} Resource</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4" data-testid="resource-form">
          <Input label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="res-name" />
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" data-testid="res-category">
              {RESOURCE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <Input label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="res-phone" />
            <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <Input label="Website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} />
            <Input label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
            <Input label="County" value={form.county} onChange={(v) => setForm({ ...form, county: v })} />
            <Input label="Hours" value={form.hours} onChange={(v) => setForm({ ...form, hours: v })} />
          </div>
          <Input label="Eligibility" value={form.eligibility} onChange={(v) => setForm({ ...form, eligibility: v })} />
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-full border border-[#E5E5E0] text-[#334155] font-medium">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="res-submit-btn" className="flex-1 py-3 rounded-full bg-[#1E3A8A] text-white font-medium disabled:opacity-60">
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Add"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Input = ({ label, value, onChange, required, type = "text", testId }) => (
  <div>
    <label className="block text-sm font-medium text-[#334155] mb-2">{label}{required && <span className="text-red-500">*</span>}</label>
    <input
      type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D]"
    />
  </div>
);

function VerificationChip({ resource }) {
  const status = classifyVerification(resource);
  if (status === "canonical") {
    const d = new Date(resource.last_verified_at);
    const label = isNaN(d.getTime())
      ? "Verified"
      : `Verified ${d.toLocaleString(undefined, { month: "short", year: "numeric" })}`;
    return (
      <span data-testid={`verification-chip-${resource.id}`} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#ECFDF5] text-[#166534] border border-[#A7F3D0]">
        <ShieldCheck className="w-3 h-3" /> {label}
      </span>
    );
  }
  if (status === "due_soon") {
    return (
      <span data-testid={`verification-chip-${resource.id}`} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]" title="Verification due within 30 days">
        <ShieldAlert className="w-3 h-3" /> Due soon
      </span>
    );
  }
  if (status === "overdue") {
    return (
      <span data-testid={`verification-chip-${resource.id}`} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#991B1B] border border-[#FCA5A5]" title="Verification overdue">
        <ShieldAlert className="w-3 h-3" /> Overdue
      </span>
    );
  }
  // Per Batch 9 of the Updates doc: "remove the blue never verified button".
  // Resources without verification history simply show no chip — only verified/due/overdue surface a chip.
  return null;
}

function VerifyModal({ resource, onClose, onVerified }) {
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/resources/${resource.id}/verify`, { notes });
      const next = data.next_verification_due ? new Date(data.next_verification_due) : null;
      toast.success(
        next
          ? `Verified — next review ${next.toLocaleDateString()}`
          : "Verified"
      );
      onVerified();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="verify-modal">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-[#E5E5E0] flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-[#166534]" />
          <div>
            <h3 className="font-display text-lg font-semibold text-[#0F172A]">Mark verified</h3>
            <p className="text-xs text-[#334155]">{resource.name}</p>
          </div>
        </div>
        <div className="px-6 py-4 space-y-3 text-sm">
          <p className="text-xs text-[#334155]">
            Confirms you&apos;ve contacted this partner, verified the listing is current, and resets the annual review clock to 365 days from now.
          </p>
          <label className="block">
            <span className="text-xs font-medium text-[#334155]">Verification notes (optional)</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              data-testid="verify-notes"
              placeholder="Spoke with Jane on phone — hours unchanged, accepts walk-ins."
              className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
            />
          </label>
        </div>
        <div className="px-6 py-4 border-t border-[#E5E5E0] bg-[#F9F8F6] flex items-center justify-end gap-2 rounded-b-2xl">
          <button onClick={onClose} disabled={saving} data-testid="verify-cancel" className="px-4 py-2 rounded-full text-xs font-medium border border-[#E5E5E0] bg-white text-[#334155]">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="verify-confirm" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-xs font-semibold bg-[#166534] text-white disabled:opacity-40">
            <ShieldCheck className="w-3.5 h-3.5" />
            {saving ? "Verifying…" : "Mark verified"}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Renders the rich partner-submission detail captured at approval time
 * (mailing address, populations served, insurance accepted, MOU acks,
 * etc.) on a Community Resource Directory card. Each subsection is
 * conditionally rendered so legacy/handcrafted resources without a
 * partner_profile stay unchanged.
 */
function PartnerProfileSection({ profile }) {
  if (!profile || typeof profile !== "object") return null;

  const pc = profile.primary_contact || {};
  const rc = profile.referral_contact || {};
  const hasPrimary = pc.name || pc.email || pc.phone || pc.title;
  const hasReferral = rc.name || rc.email || rc.phone;
  const methods = [
    ...(profile.preferred_referral_methods || []),
    profile.preferred_referral_method_other ? `Other: ${profile.preferred_referral_method_other}` : null,
  ].filter(Boolean);

  const rows = [
    ["Mailing address",    profile.mailing_address],
    ["Service area",       profile.service_area],
    ["After-hours availability", profile.after_hours_availability],
    ["Populations served", profile.populations_served],
    ["Age requirements",   profile.age_requirements],
    ["Cost / payment",     profile.cost_payment_info],
    ["Insurance accepted", profile.insurance_accepted],
    ["Required documents", profile.required_documents],
    ["Referral requirements", profile.referral_requirements],
    ["Estimated wait time", profile.estimated_wait_time],
  ].filter(([, v]) => v && String(v).trim());

  const anything = rows.length || hasPrimary || hasReferral || methods.length || (profile.mou_acks && Object.keys(profile.mou_acks).length);
  if (!anything) return null;

  const signed = profile.signature?.signed_at;
  const signedBy = profile.signature?.name;

  return (
    <div className="mt-3 pt-3 border-t border-[#F0EFEB] space-y-3 text-xs" data-testid="partner-profile-section">
      {rows.length > 0 && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1">
          {rows.map(([label, val]) => (
            <div key={label} className="contents">
              <dt className="text-[#1E3A8A] font-medium whitespace-nowrap">{label}:</dt>
              <dd className="text-[#334155]">{val}</dd>
            </div>
          ))}
        </dl>
      )}

      {hasPrimary && (
        <div className="rounded-lg bg-[#F9F8F6] p-2.5">
          <div className="hw-eyebrow text-[10px] mb-1">Primary contact</div>
          <div className="text-[#0F172A] font-medium">
            {pc.name || "—"}{pc.title ? ` · ${pc.title}` : ""}
          </div>
          <div className="text-[#334155]">{[pc.email, pc.phone].filter(Boolean).join(" · ") || "—"}</div>
        </div>
      )}

      {hasReferral && (
        <div className="rounded-lg bg-[#F9F8F6] p-2.5">
          <div className="hw-eyebrow text-[10px] mb-1">Referral contact</div>
          <div className="text-[#0F172A] font-medium">{rc.name || "—"}</div>
          <div className="text-[#334155]">{[rc.email, rc.phone].filter(Boolean).join(" · ") || "—"}</div>
        </div>
      )}

      {methods.length > 0 && (
        <div>
          <div className="hw-eyebrow text-[10px] mb-1">Preferred referral methods</div>
          <div className="flex flex-wrap gap-1.5">
            {methods.map((m) => (
              <span key={m} className="px-2 py-0.5 rounded-full bg-[#E0E7FF] text-[#1E3A8A] text-[10px]">{m}</span>
            ))}
          </div>
        </div>
      )}

      {profile.mou_acks && Object.keys(profile.mou_acks).length > 0 && (
        <details className="rounded-lg border border-[#E5E5E0] bg-white">
          <summary className="cursor-pointer px-2.5 py-1.5 text-[#1E3A8A] font-medium">
            Partnership agreement {signed ? `· signed ${new Date(signed).toLocaleDateString()}` : ""}
          </summary>
          <div className="px-2.5 py-2 space-y-1 border-t border-[#F0EFEB]">
            {Object.entries(profile.mou_acks).map(([k, v]) => (
              <div key={k} className="flex items-start gap-2 text-[#334155]">
                <span className={v ? "text-[#166534]" : "text-[#991B1B]"}>{v ? "✓" : "✗"}</span>
                <span className="font-mono text-[10px]">{k}</span>
                <span className={v ? "text-[#166534]" : "text-[#991B1B]"}>{v ? "acknowledged" : "missing"}</span>
              </div>
            ))}
            {signedBy && (
              <div className="pt-1 mt-1 border-t border-[#F0EFEB] text-[#1E3A8A]">
                Signed by <strong>{signedBy}</strong>{profile.signature?.title ? ` · ${profile.signature.title}` : ""}
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  );
}

